import { Box } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';

type ResizeHandleProps = {
    value: number;
    min: number;
    max: number;
    onChange: (width: number) => void;
    onCommit: (width: number) => void;
    /**
     * Which side of the handle the resized pane sits on. With 'after', dragging right shrinks it;
     * with 'before', dragging right grows it.
     */
    pane: 'before' | 'after';
    /** Width of the gap between the panes, which is the whole grab area. */
    width: number;
};

/**
 * The draggable seam between two panes.
 *
 * Occupies the gap between them: the whole gap is the grab area, and a centred line appears while
 * the pointer is over it so the seam reads as a handle only when it is about to be used.
 * Pointer-only.
 */
export default function ResizeHandle({
    value,
    min,
    max,
    onChange,
    onCommit,
    pane,
    width,
}: ResizeHandleProps) {
    const dragStart = useRef<{ x: number; width: number } | null>(null);
    // Read by the move handler, which is bound once per drag rather than per render.
    const latest = useRef({ min, max, onChange, onCommit, pane });
    latest.current = { min, max, onChange, onCommit, pane };

    const widthFromDelta = useCallback((clientX: number) => {
        const start = dragStart.current;
        if (!start) {
            return undefined;
        }
        const { min, max, pane } = latest.current;
        const delta = clientX - start.x;
        const signed = pane === 'after' ? -delta : delta;
        return Math.min(max, Math.max(min, start.width + signed));
    }, []);

    useEffect(() => {
        const onPointerMove = (event: PointerEvent) => {
            const width = widthFromDelta(event.clientX);
            if (width !== undefined) {
                latest.current.onChange(width);
            }
        };

        const onPointerUp = (event: PointerEvent) => {
            const width = widthFromDelta(event.clientX);
            if (width === undefined) {
                return;
            }
            dragStart.current = null;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            latest.current.onCommit(width);
        };

        document.addEventListener('pointermove', onPointerMove);
        document.addEventListener('pointerup', onPointerUp);
        document.addEventListener('pointercancel', onPointerUp);
        return () => {
            document.removeEventListener('pointermove', onPointerMove);
            document.removeEventListener('pointerup', onPointerUp);
            document.removeEventListener('pointercancel', onPointerUp);
            // Unmounting mid-drag (before pointerup) would otherwise leave the
            // whole app with the resize cursor and unselectable until reload.
            if (dragStart.current) {
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
                dragStart.current = null;
            }
        };
    }, [widthFromDelta]);

    const onPointerDown = useCallback(
        (event: React.PointerEvent) => {
            event.preventDefault();
            dragStart.current = { x: event.clientX, width: value };
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        },
        [value]
    );

    return (
        <Box
            onPointerDown={onPointerDown}
            sx={{
                width,
                flexShrink: 0,
                cursor: 'col-resize',
                position: 'relative',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: '50%',
                    width: '2px',
                    marginLeft: '-1px',
                    borderRadius: 1,
                    backgroundColor: 'transparent',
                    transition: 'background-color 150ms',
                },
                '&:hover::after': { backgroundColor: 'primary.main' },
            }}
        />
    );
}
